// Administração do acesso ao Nível de Serviço, por usuário: liga/desliga
// (feature) + refinamento de BU, carteira e nível de detalhe. O recorte de
// indústria/seller do tenant NÃO se configura aqui — vem da empresa
// (company.id_external_bi + tipo). Tudo é conveniência de UI: o backend revalida.
import {useEffect, useMemo, useState} from 'react'
import Select from 'react-select'
// '../../../api' resolveria para o api.ts legado; o módulo novo é api/index.ts
import * as API from '../../../api/index'
import {ServiceLevelCatalog, ServiceLevelScope} from '../../../api/index'

const FEATURE_NAME = 'service-level'

const NIVEIS = [
  {value: 1, label: 'N1 · Somente números', desc: 'KPIs consolidados. Sem lista de sellers, indústrias ou pedidos.'},
  {value: 2, label: 'N2 · Recortes', desc: 'N1 + fechamento por seller/indústria/BU e resumo de cada um.'},
  {value: 3, label: 'N3 · Detalhe completo', desc: 'N2 + lista de pedidos, exportação e detalhe item a item.'},
]

type Opcao = {value: number; label: string}

export const ServiceLevelAccess = () => {
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const [usuarios, setUsuarios] = useState<any[]>([])
  const [idFeature, setIdFeature] = useState<number | null>(null)
  const [comAcesso, setComAcesso] = useState<Set<number>>(new Set())
  const [catalogo, setCatalogo] = useState<ServiceLevelCatalog | null>(null)

  const [usuarioSel, setUsuarioSel] = useState<any>(null)
  const [escopo, setEscopo] = useState<ServiceLevelScope>({
    bus: null,
    sellers: null,
    detail_level: 3,
  })

  // carga inicial: usuários da empresa + quem já tem a feature + catálogo do ca_ns
  useEffect(() => {
    ;(async () => {
      try {
        const res = await API.getUserFeatures()
        const data = res.data
        const feature = (data.features || []).find((f: any) => f.name === FEATURE_NAME)
        if (!feature) {
          setErro(
            'A funcionalidade "Nível de Serviço" não está habilitada para esta empresa. Peça a um superadmin para liberá-la em Empresas → Funcionalidades.'
          )
          setCarregando(false)
          return
        }
        setIdFeature(feature.id_feature)
        setUsuarios(data.users || [])
        setComAcesso(
          new Set(
            (data.permissions || [])
              .filter((p: any) => p.id_feature === feature.id_feature)
              .map((p: any) => p.id_user)
          )
        )
        setCatalogo(await API.getServiceLevelCatalog())
      } catch (e: any) {
        setErro('Não foi possível carregar os dados do Nível de Serviço.')
      } finally {
        setCarregando(false)
      }
    })()
  }, [])

  // troca de usuário → carrega o escopo salvo dele
  useEffect(() => {
    if (!usuarioSel) return
    setAviso(null)
    API.getServiceLevelScope(usuarioSel.guid)
      .then(setEscopo)
      .catch(() => setEscopo({bus: null, sellers: null, detail_level: 3}))
  }, [usuarioSel])

  const opcoesUsuarios: Opcao[] = useMemo(
    () => usuarios.map((u) => ({value: u.id_user, label: `${u.name} · ${u.email}`, ...u})) as any,
    [usuarios]
  )

  // BUs das indústrias que a EMPRESA enxerga (para empresa distribuidora, todas)
  const opcoesBus: Opcao[] = useMemo(() => {
    const bus = catalogo?.bus || []
    const empresa = escopo.empresa
    const idsIndustria = empresa?.tipo === 'INDUSTRIA' ? empresa.itens.map((i) => i.id) : null
    const filtradas = idsIndustria?.length ? bus.filter((b) => idsIndustria.includes(b.industria_id)) : bus
    return filtradas.map((b) => {
      const ind = catalogo?.industrias.find((i) => i.id === b.industria_id)
      return {value: b.id, label: ind ? `${ind.nome} · ${b.nome}` : b.nome}
    })
  }, [catalogo, escopo.empresa])

  const opcoesSellers: Opcao[] = useMemo(() => {
    const todos = catalogo?.sellers || []
    const empresa = escopo.empresa
    const permitidos = empresa?.tipo === 'DISTRIBUIDOR' ? empresa.itens.map((i) => i.id) : null
    const lista = permitidos?.length ? todos.filter((s) => permitidos.includes(s.id)) : todos
    return lista.map((s) => ({value: s.id, label: `${s.nome} (${s.uf || '—'})`}))
  }, [catalogo, escopo.empresa])

  const temAcesso = usuarioSel ? comAcesso.has(usuarioSel.id_user) : false

  const alternarAcesso = async () => {
    if (!usuarioSel || !idFeature) return
    setSalvando(true)
    setAviso(null)
    try {
      const payload = {guid: usuarioSel.guid, id_feature: idFeature}
      if (temAcesso) {
        await API.removeFeatureUserPermission(payload)
        comAcesso.delete(usuarioSel.id_user)
      } else {
        await API.addFeatureUserPermission(payload)
        comAcesso.add(usuarioSel.id_user)
      }
      setComAcesso(new Set(comAcesso))
      setAviso(temAcesso ? 'Acesso removido.' : 'Acesso liberado.')
    } catch {
      setAviso('Falha ao alterar o acesso.')
    } finally {
      setSalvando(false)
    }
  }

  const salvarEscopo = async () => {
    if (!usuarioSel) return
    setSalvando(true)
    setAviso(null)
    try {
      const salvo = await API.putServiceLevelScope(usuarioSel.guid, escopo)
      setEscopo(salvo)
      setAviso('Escopo salvo.')
    } catch {
      setAviso('Falha ao salvar o escopo.')
    } finally {
      setSalvando(false)
    }
  }

  const valorMulti = (ids: number[] | null, opcoes: Opcao[]) =>
    ids?.length ? opcoes.filter((o) => ids.includes(o.value)) : []

  if (carregando) return <div className='text-muted'>Carregando…</div>
  if (erro) return <div className='alert alert-warning mb-0'>{erro}</div>

  return (
    <div className='row'>
      <div className='col-lg-4 mb-5'>
        <label className='form-label fw-bold'>Usuário</label>
        <Select
          options={opcoesUsuarios}
          value={usuarioSel}
          onChange={(u) => setUsuarioSel(u)}
          placeholder='Selecione um usuário'
          noOptionsMessage={() => 'Nenhum usuário'}
        />
        <div className='text-muted fs-8 mt-2'>
          {comAcesso.size} usuário(s) com acesso ao Nível de Serviço.
        </div>
      </div>

      {usuarioSel && (
        <div className='col-lg-8'>
          <div className='d-flex align-items-center justify-content-between border rounded p-4 mb-5'>
            <div>
              <div className='fw-bold'>Acesso ao painel</div>
              <div className='text-muted fs-8'>
                {temAcesso
                  ? 'Este usuário vê o Nível de Serviço no menu e na home.'
                  : 'Este usuário não tem acesso ao Nível de Serviço.'}
              </div>
            </div>
            <button
              className={`btn btn-sm ${temAcesso ? 'btn-light-danger' : 'btn-primary'}`}
              onClick={alternarAcesso}
              disabled={salvando}
            >
              {temAcesso ? 'Remover acesso' : 'Liberar acesso'}
            </button>
          </div>

          <div className={temAcesso ? '' : 'opacity-50 pe-none'}>
            <div className='mb-4 border rounded p-4 bg-light'>
              <div className='fw-bold mb-1'>Escopo herdado da empresa</div>
              {escopo.empresa ? (
                <>
                  <div className='text-muted fs-8 mb-2'>
                    {escopo.empresa.company_name} · {escopo.empresa.tipo_nome} — definido em Empresas →
                    ID externo (BI), não se configura por usuário.
                  </div>
                  {escopo.empresa.itens.length ? (
                    <div className='d-flex flex-wrap gap-2'>
                      {escopo.empresa.itens.map((i) => (
                        <span className='badge badge-light-primary' key={i.id}>
                          {i.nome}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className='text-danger fs-8'>
                      {escopo.empresa.sem_correspondencia
                        ? `Os IDs externos da empresa (${escopo.empresa.ids_externos.join(', ')}) não correspondem a nenhum ${
                            escopo.empresa.tipo === 'INDUSTRIA' ? 'indústria' : 'seller'
                          } com dados no Nível de Serviço.`
                        : 'A empresa não tem ID externo (BI) configurado — o usuário não verá dado nenhum.'}
                    </div>
                  )}
                </>
              ) : (
                <div className='text-muted fs-8'>Sem empresa vinculada.</div>
              )}
            </div>

            <div className='mb-4'>
              <label className='form-label fw-bold'>Unidades de negócio (BU)</label>
              <Select
                isMulti
                options={opcoesBus}
                value={valorMulti(escopo.bus, opcoesBus)}
                onChange={(v) => setEscopo((e) => ({...e, bus: v.length ? v.map((o) => o.value) : null}))}
                placeholder='Todas as BUs'
              />
              <div className='text-muted fs-8 mt-1'>
                Vazio = todas. O indicador de prazo é por pedido (que pode ter várias BUs), então não
                é recortado por BU.
              </div>
            </div>

            <div className='mb-4'>
              <label className='form-label fw-bold'>Carteira de sellers</label>
              <Select
                isMulti
                options={opcoesSellers}
                value={valorMulti(escopo.sellers, opcoesSellers)}
                onChange={(v) =>
                  setEscopo((e) => ({...e, sellers: v.length ? v.map((o) => o.value) : null}))
                }
                placeholder='Todos os sellers'
              />
              <div className='text-muted fs-8 mt-1'>Vazio = todos.</div>
            </div>

            <div className='mb-4'>
              <label className='form-label fw-bold'>Nível de detalhe</label>
              {NIVEIS.map((n) => (
                <div className='form-check mb-2' key={n.value}>
                  <input
                    className='form-check-input'
                    type='radio'
                    id={`sl-nivel-${n.value}`}
                    checked={escopo.detail_level === n.value}
                    onChange={() => setEscopo((e) => ({...e, detail_level: n.value as 1 | 2 | 3}))}
                  />
                  <label className='form-check-label' htmlFor={`sl-nivel-${n.value}`}>
                    <span className='fw-bold'>{n.label}</span>
                    <span className='text-muted fs-8 d-block'>{n.desc}</span>
                  </label>
                </div>
              ))}
            </div>

            <div className='d-flex align-items-center gap-3'>
              <button className='btn btn-primary' onClick={salvarEscopo} disabled={salvando}>
                Salvar escopo
              </button>
              {aviso && <span className='text-muted fs-7'>{aviso}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
